'use client';

import classNames from 'classnames';
import { createRef, useEffect, useState } from 'react';
import { defineMessages } from 'react-intl';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import useIsVisible from '@alltrails/core/hooks/useIsVisible';
import CheckFilled from '@alltrails/denali/icons/CheckCircleFilled';
import VerifiedFilled from '@alltrails/denali/icons/VerifiedFilled';
import styles from './styles/styles.module.scss';

// https://www.figma.com/file/mh7ECfwLfHr8jTIaAYOS80/Trail-Cards-Handoff?type=design&node-id=6-22407&mode=design&t=vDaTkNPuaJn0XTJ6-0

const AUTO_EXPAND_STORAGE_KEY = 'trail-standard-card-completion-badge-auto-expanded';
const AUTO_EXPAND_MAX_COUNT = 1;

const messages = defineMessages({
  COMPLETED: {
    defaultMessage: 'Completed'
  },
  VERIFIED: {
    defaultMessage: 'Verified completed'
  }
});

type CompletionBadgeProps = {
  isAutoExpanded?: boolean;
  isCompleted?: boolean;
  isVerified?: boolean;
  onClick?: () => void;
};

const CompletionBadge = ({ isAutoExpanded = false, isCompleted = false, isVerified = false, onClick }: CompletionBadgeProps) => {
  // Always collapsed by default, even if isAutoExpanded is true. Let the hook below handle auto-expanding so the animation kicks in.
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [hasEverAutoExpanded, setHasEverAutoExpanded] = useState(false);
  const ref = createRef<HTMLButtonElement>();
  const isVisible = useIsVisible(ref, { threshold: 1 });

  const {
    formattedDefaultMessages: { COMPLETED, VERIFIED }
  } = useFormatMessage(messages);

  useEffect(() => {
    // Not auto-expanding or not visible on screen. Nothing to do at this time.
    if (!isAutoExpanded || !isVisible) {
      return;
    }

    // This specific instance of this component has already auto-expanded. Don't do it again for its lifetime.
    if (hasEverAutoExpanded) {
      return;
    }

    // We've already shown this behavior the maximum number of times.
    const count = Number(localStorage.getItem(AUTO_EXPAND_STORAGE_KEY) || 0);
    if (count >= AUTO_EXPAND_MAX_COUNT) {
      return;
    }

    // Don't auto-expand again for this component's lifetime.
    setHasEverAutoExpanded(true);
    // Auto-expand the component to trigger an animation.
    setIsCollapsed(false);

    // Increment the number of times we've shown this behavior.
    localStorage.setItem(AUTO_EXPAND_STORAGE_KEY, String(count + 1));
  }, [hasEverAutoExpanded, isAutoExpanded, isVisible]);

  if (!isCompleted && !isVerified) {
    return <div />; // Useful for flex placement
  }

  const text = isVerified ? VERIFIED : COMPLETED;

  return (
    <button
      aria-label={text}
      className={classNames(styles.container, { [styles.isExpanded]: !isCollapsed })}
      onClick={() => {
        setIsCollapsed(!isCollapsed);
        onClick?.();
      }}
      type="button"
      ref={ref}
    >
      {isVerified ? <VerifiedFilled size="sm" color="--color-brand-onyx" /> : <CheckFilled size="sm" color="--color-brand-onyx" />}
      <span className={styles.text}>{isCollapsed ? '' : text}</span>
    </button>
  );
};

export default CompletionBadge;
